// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  HighlightOffOutlined,
  SubdirectoryArrowRight,
  SwapVerticalCircleOutlined,
} from '@/constants/hardfin-standard-icons';
import { pluralize } from '@/utilities/pluralize';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface WorkflowChange {
  type: 'deletion' | 'relativeOrderChange';
  stageName: string;
  details: string;
  stageId?: string;
  migrationTargetId?: string;
}

// Simplified data model for dropdown options - only what we need for display
interface DropdownStage {
  name: string;
  orderIndex: number;
}

interface WorkflowStageForComparison {
  id: string;
  name: string;
  orderIndex: number;
  inUseCount: number | null;
  isNew?: boolean;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (migrations?: Record<string, string>) => void;
  originalStages: WorkflowStageForComparison[];
  modifiedStages: WorkflowStageForComparison[];
  isLoading?: boolean;
}

export function WorkflowChangeConfirmationDialog(props: Props) {
  const {
    isOpen,
    onClose,
    onConfirm,
    originalStages,
    modifiedStages,
    isLoading = false,
  } = props;

  // Get active stages for dropdown (safe data model with no temp IDs)
  const getActiveDropdownStages = useCallback((): DropdownStage[] => {
    return modifiedStages
      .map((stage) => ({
        name: stage.name,
        orderIndex: stage.orderIndex,
      }))
      .sort((a, b) => a.orderIndex - b.orderIndex);
  }, [modifiedStages]);

  // State to track migration targets for deleted stages (maps stage ID to target stage name)
  const [migrationTargets, setMigrationTargets] = useState<
    Record<string, string>
  >({});

  // Function to get default migration target for a deleted stage (returns stage name)
  const getDefaultMigrationTarget = useCallback(
    (deletedStage: WorkflowStageForComparison): string | undefined => {
      // Include all stages (existing with IDs and new without IDs)
      const remainingStages = modifiedStages;
      if (remainingStages.length === 0) return undefined;

      // Map remaining stages back to their original order indices for comparison
      const remainingStagesWithOriginalOrder = remainingStages.map(
        (modifiedStage) => {
          const originalStage = originalStages.find(
            (orig) => orig.id === modifiedStage.id
          );
          return {
            ...modifiedStage,
            originalOrderIndex:
              originalStage?.orderIndex || modifiedStage.orderIndex,
          };
        }
      );

      // Try to find the previous stage (lower original orderIndex)
      const previousStage = remainingStagesWithOriginalOrder
        .filter((stage) => stage.originalOrderIndex < deletedStage.orderIndex)
        .sort((a, b) => b.originalOrderIndex - a.originalOrderIndex)[0];

      if (previousStage) {
        return previousStage.name;
      }

      // If no previous stage, use the next stage (higher original orderIndex)
      const nextStage = remainingStagesWithOriginalOrder
        .filter((stage) => stage.originalOrderIndex > deletedStage.orderIndex)
        .sort((a, b) => a.originalOrderIndex - b.originalOrderIndex)[0];

      return nextStage?.name;
    },
    [modifiedStages, originalStages]
  );

  const calculateChanges = useCallback((): WorkflowChange[] => {
    const changes: WorkflowChange[] = [];

    // Create efficient lookups
    const modifiedMap = new Map(
      modifiedStages
        .filter((stage) => !stage.isNew) // Only existing stages
        .map((stage) => [stage.id, stage])
    );

    // Find deletions
    originalStages.forEach((original) => {
      if (!modifiedMap.has(original.id)) {
        const defaultTarget = getDefaultMigrationTarget(original);
        changes.push({
          type: 'deletion',
          stageName: original.name,
          stageId: original.id,
          migrationTargetId: defaultTarget,
          details: `"${original.name}" will be deleted`,
        });
      }
    });

    // Find position changes - simply check if order changed
    originalStages.forEach((original) => {
      const modified = modifiedMap.get(original.id);
      if (modified && original.orderIndex !== modified.orderIndex) {
        changes.push({
          type: 'relativeOrderChange',
          stageName: original.name,
          stageId: original.id,
          details: `"${original.name}" moved from stage ${original.orderIndex} to stage ${modified.orderIndex}`,
        });
      }
    });

    return changes;
  }, [originalStages, modifiedStages, getDefaultMigrationTarget]);

  const allChanges = useMemo(() => calculateChanges(), [calculateChanges]);

  // Filter changes to only show those that affect projects
  const changes = useMemo(() => {
    return allChanges.filter((change) => {
      if (change.stageId) {
        const originalStage = originalStages.find(
          (s) => s.id === change.stageId
        );
        return (originalStage?.inUseCount || 0) > 0;
      }
      return false;
    });
  }, [allChanges, originalStages]);

  const hasChanges = changes.length > 0;

  // Initialize migration targets when dialog opens (use all changes, not just filtered ones)
  useEffect(() => {
    if (isOpen && allChanges.length > 0) {
      const deletions = allChanges.filter(
        (change) => change.type === 'deletion'
      );
      const initialTargets: Record<string, string> = {};

      deletions.forEach((deletion) => {
        if (deletion.stageId && deletion.migrationTargetId) {
          initialTargets[deletion.stageId] = deletion.migrationTargetId;
        }
      });

      setMigrationTargets(initialTargets);
    }
  }, [isOpen, allChanges]);

  const handleMigrationTargetChange = (
    deletedStageId: string,
    targetStageName: string
  ) => {
    setMigrationTargets((prev) => ({
      ...prev,
      [deletedStageId]: targetStageName,
    }));
  };

  const handleConfirm = () => {
    onConfirm(migrationTargets);
  };

  // Don't show dialog if no changes
  if (!hasChanges) {
    return null;
  }

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Confirm workflow changes</DialogTitle>

      <DialogContent sx={{ py: 0 }}>
        <Alert severity="warning">
          The following changes will affect existing projects
        </Alert>

        <List>
          {changes.map((change, index) => (
            <ListItem
              key={index}
              sx={{
                py: 2,
                alignItems: 'flex-start',
                flexDirection: change.type === 'deletion' ? 'column' : 'row',
              }}
            >
              {change.type === 'deletion' ? (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      width: '100%',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                      <HighlightOffOutlined color="primary" fontSize="large" />
                    </ListItemIcon>
                    <ListItemText
                      primary={change.details}
                      secondary={
                        change.stageId &&
                        (() => {
                          const originalStage = originalStages.find(
                            (s) => s.id === change.stageId
                          );
                          const count = originalStage?.inUseCount || 0;
                          return pluralize(count, {
                            singular: 'project will be affected',
                            plural: 'projects will be affected',
                          });
                        })()
                      }
                      sx={{
                        flex: '1 1 auto',
                        minWidth: 0, // Allow text to shrink
                        '& .MuiListItemText-primary': {
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                        },
                        '& .MuiListItemText-secondary': {
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                        },
                      }}
                    />
                  </Box>
                  {change.stageId && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        mt: 1,
                        pl: 7,
                      }}
                    >
                      <SubdirectoryArrowRight
                        color="action"
                        fontSize="medium"
                        sx={{ mr: 1 }}
                      />
                      <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                        <FormControl size="small" fullWidth>
                          <InputLabel>Change projects to</InputLabel>
                          <Select
                            value={migrationTargets[change.stageId] || ''}
                            label="Change projects to"
                            onChange={(e) =>
                              handleMigrationTargetChange(
                                change.stageId!,
                                e.target.value
                              )
                            }
                            sx={{
                              minWidth: 0,
                              '& .MuiSelect-select': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                minWidth: 0,
                                maxWidth: '100%',
                              },
                              '& .MuiInputBase-input': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              },
                            }}
                            renderValue={(selected) => {
                              // Find the selected stage for display
                              const activeStages = getActiveDropdownStages();
                              const selectedStage = activeStages.find(
                                (stage) => stage.name === selected
                              );
                              if (!selectedStage) return selected;
                              return `Stage ${selectedStage.orderIndex}: ${selectedStage.name}`;
                            }}
                          >
                            {getActiveDropdownStages().map((stage, index) => (
                              <MenuItem key={index} value={stage.name}>
                                Stage {stage.orderIndex}: {stage.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </Box>
                  )}
                </>
              ) : (
                <>
                  <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                    <SwapVerticalCircleOutlined
                      color="primary"
                      fontSize="large"
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={change.details}
                    secondary={
                      change.stageId &&
                      (() => {
                        const originalStage = originalStages.find(
                          (s) => s.id === change.stageId
                        );
                        const count = originalStage?.inUseCount || 0;
                        return pluralize(count, {
                          singular: 'project will be affected',
                          plural: 'projects will be affected',
                        });
                      })()
                    }
                    sx={{
                      flex: '1 1 auto',
                      minWidth: 0, // Allow text to shrink
                      '& .MuiListItemText-primary': {
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                      },
                      '& .MuiListItemText-secondary': {
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                      },
                    }}
                  />
                </>
              )}
            </ListItem>
          ))}
        </List>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color={'primary'}
          disabled={isLoading}
        >
          Confirm changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}
